import { BINotification } from '@pulsepointinc/bi-components';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useFilters } from '../../../../../../hooks/useFilters';
import useOptions from '../../../../../../hooks/useOptions';
import type { ExpressionType, Filter } from '../../../../../../stores/filters';
import { useUserData } from '../../../hooks/useUserData';
import { StyledBISelect } from './style';

interface LinkedFilterProps {
  filterName: string;
  view: string;
  fields: string[];
  pseudoAdvertiserIdFilterKey: string;
  warningMessage?: string;
  onChange?: (filter: Filter) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  pseudoAccountIdFilterKey: string;
}

const LinkedFilter: FC<LinkedFilterProps> = ({
  filterName,
  view,
  fields,
  pseudoAdvertiserIdFilterKey,
  warningMessage,
  onChange,
  onLoadingChange,
  pseudoAccountIdFilterKey,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [showWarning, setShowWarning] = useState(false);
  const { userData } = useUserData();

  const { filters } = useFilters();
  const filtersList = filters ? (Object.values(filters) as Filter[]) : [];
  const filterState = filtersList.find((filter) => filter.filterName === filterName);
  const filterExpression = filterState?.expression || '';

  const includeHcp365AccountFilter = filterName === 'smart_pixel_collection_filter';

  const { options, isLoading } = useOptions({
    extraExpression: filterExpression,
    view,
    fields,
    pseudoAdvertiserIdFilterKey,
    pseudoAccountIdFilterKey,
    accountID: userData.acctId || '',
    includeHcp365AccountFilter,
  });

  const optionSuggestion = useMemo(() => {
    const validSuggestions = options.filter((option) => option?.value);
    if (!searchTerm) return validSuggestions;
    return validSuggestions.filter((option) => option.value.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [searchTerm, options]);

  const handleChangeFilter = (value: string | string[]) => {
    const filterList = Array.isArray(value) ? value : [value];
    const expressionType: ExpressionType = 'target';
    const filter: Filter = {
      filterName,
      expression: filterList.join(','),
      enabled: true,
      expressionType,
    };

    onChange?.(filter);
    setSelectedValues(filterList);
    setShowWarning(!!warningMessage);
  };

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    if (filterExpression) {
      const selected = filterExpression.split(',').map((expr) => expr.trim());
      const matchedValues = selected.filter((value) => options.some((option) => option?.value === value));
      setSelectedValues(matchedValues);
    }
  }, [filterExpression, options]);

  const handleFilter = (filterTerm: string) => {
    setSearchTerm(filterTerm);
  };

  const validOptions = useMemo(
    () =>
      optionSuggestion.map((option) => ({
        ...option,
        value: option.value.toString(),
      })),
    [optionSuggestion],
  );

  return (
    <>
      <StyledBISelect
        options={validOptions}
        placeholder={selectedValues.length ? '' : 'any value'}
        listLayout={{ width: 'auto' }}
        isLoading={isLoading}
        onFilter={handleFilter}
        values={selectedValues}
        onChange={handleChangeFilter}
        isFilterable
        multiSelect
        filterName={filterName}
      />
      {showWarning && warningMessage && <BINotification text={warningMessage} intent="warning" />}
    </>
  );
};

export default LinkedFilter;
